import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import {
  GetStaticComponentProps,
  Placeholder,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/types/component-props';
import ModalWrapper, { AnimationStyle, ModalSize } from 'src/helpers/ModalWrapper/ModalWrapper';
import { useEffect, useState } from 'react';
import { getEnum } from 'lib/utils/get-enum';
import Component from 'src/helpers/Component/Component';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { IBasePopupTrigger } from 'src/helpers/PopupTriggers/BasePopupTrigger';
import { popupTriggerFactory, PopupTriggerItem } from 'src/temp/popupTriggerFactory';

export type PopUpModalProps =
  Feature.EnterpriseWeb.Enterprise.Components.Modal.PopUpModal.PopUpModal & ComponentProps;

type PopupTriggerCleanup = () => void;

const PopUpModal = (props: PopUpModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { currentScreenWidth } = useCurrentScreenType();
  const hasScreenWidth = currentScreenWidth > 0;
  const isMobile = hasScreenWidth && currentScreenWidth < getBreakpoint('md');

  const desktopAnimationStyle =
    getEnum<AnimationStyle>(props.fields?.desktopAnimationStyle) || null;
  const mobileAnimationStyle = getEnum<AnimationStyle>(props.fields?.mobileAnimationStyle) || null;
  const modalSize = getEnum<ModalSize>(props.fields?.modalSize) ?? 'large';
  const displayCloseBar = props.fields?.displayCloseBar?.value || false;

  const popupId = props.rendering?.dataSource;

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!hasScreenWidth) {
      return;
    }

    if (isMobile) {
      return;
    }

    if (!props.fields?.triggerRules?.length) {
      return;
    }

    const triggerHandlers: Array<IBasePopupTrigger | undefined> = props.fields.triggerRules.map(
      (trigger: PopupTriggerItem) => popupTriggerFactory(trigger)
    );

    const cleanups = triggerHandlers
      .map((triggerHandler): void | PopupTriggerCleanup =>
        triggerHandler?.register({
          popupId,
          onTrigger: () => {
            setIsModalOpen(true);
          },
        })
      )
      .filter(
        (cleanup: void | PopupTriggerCleanup): cleanup is PopupTriggerCleanup =>
          typeof cleanup === 'function'
      );

    return () => {
      cleanups.forEach((cleanup: PopupTriggerCleanup) => cleanup());
    };
  }, [currentScreenWidth, hasScreenWidth, isMobile, popupId, props.fields?.triggerRules]);

  return (
    <Component dataComponent="modal/popupmodal" {...props}>
      {isModalOpen && (
        <ModalWrapper
          size={modalSize}
          handleClose={handleCloseModal}
          modalLabel="Popup Modal"
          isModalOpen={isModalOpen}
          desktopAnimationStyle={desktopAnimationStyle}
          mobileAnimationStyle={mobileAnimationStyle}
          displayCloseBar={displayCloseBar}
        >
          <Placeholder
            isGenericModal={isModalOpen}
            name="components"
            rendering={props.rendering}
            render={(components) => {
              return components.map((component, index) => <div key={index}>{component}</div>);
            }}
          />
        </ModalWrapper>
      )}
    </Component>
  );
};

type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  return {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
    },
  };
};

export default withDatasourceCheck()<PopUpModalProps>(PopUpModal);
